// Notification delivery preferences; scheduled-payment reviews use the weekly briefing settings.
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { confirmDialog } from '@/components/ui/ConfirmDialog';
import { useWorkspaceStore } from '@/stores/workspace';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Prefs {
  user_id: string;
  workspace_id: string;
  enabled: number; // 0 | 1 from SQLite
  morning_briefing_local_time: string; // 'HH:MM'
  quiet_start_local_time: string;
  quiet_end_local_time: string;
  realtime_types: string; // JSON-stringified array (parse client-side)
}

interface DeviceToken {
  id: string;
  user_id: string;
  platform: string;
  label: string | null;
  last_seen_at: number;
  last_used_at: number | null;
  failure_count: number;
  user_name: string | null;
  /** Server-set: true if this token belongs to the currently-logged-in user.
   *  Used to render "(你)" suffix + gate the remove button (you can only
   *  remove your own devices, not family members'). */
  is_self: boolean;
}

const ALL_REALTIME_TYPES = [
  'duplicate_receipt',
  'pay_yourself_paycheck',
  'credit_high_critical',
] as const;

// ─── Sub-components ───────────────────────────────────────────────────────────

function TimeRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex items-center justify-between text-sm">
      <span>{label}</span>
      <input
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-[44px] rounded border border-input bg-background px-2 py-1 text-sm text-foreground dark:[color-scheme:dark]"
      />
    </label>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function NotificationsSection() {
  const { t } = useTranslation();
  const ws = useWorkspaceStore((s) => s.current);
  const qc = useQueryClient();

  // ── Queries ──────────────────────────────────────────────────────────────

  const prefsQ = useQuery({
    queryKey: ['notification-prefs', ws?.id],
    queryFn: () => api.get<Prefs>('/push/notification-preferences'),
    enabled: !!ws,
  });

  const tokensQ = useQuery({
    queryKey: ['device-tokens', ws?.id],
    queryFn: () => api.get<{ tokens: DeviceToken[] }>('/push/device-tokens'),
    enabled: !!ws,
  });

  // ── Mutations ─────────────────────────────────────────────────────────────

  const updatePrefs = useMutation({
    mutationFn: (patch: Partial<Prefs> | { realtime_types: string[] }) =>
      api.put<Prefs>('/push/notification-preferences', patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notification-prefs', ws?.id] });
    },
  });

  const deleteToken = useMutation({
    mutationFn: (id: string) => api.del(`/push/device-tokens/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['device-tokens', ws?.id] });
    },
  });

  // ── Derived state ─────────────────────────────────────────────────────────

  const prefs = prefsQ.data;
  const enabled = prefs ? Boolean(prefs.enabled) : false;

  let realtimeArr: string[] = [];
  if (prefs?.realtime_types) {
    try {
      realtimeArr = JSON.parse(prefs.realtime_types) as string[];
    } catch {
      realtimeArr = [];
    }
  }

  // ── Handlers ──────────────────────────────────────────────────────────────

  async function handleToggle(next: boolean) {
    if (next) {
      // Enable flow
      if (!Capacitor.isNativePlatform()) {
        toast.error(t('notifications.errorNotNative'));
        return;
      }

      try {
        const permResult = await PushNotifications.requestPermissions();
        if (permResult.receive !== 'granted') {
          toast.error(t('notifications.errorPermissionDenied'));
          return;
        }

        await PushNotifications.register();
        // Previously this mutateAsync threw silently on PUT failure (network
        // / 4xx) — UI showed no toast, user assumed toggle was on, but DB
        // never persisted enabled=1. Observed 2026-05-28: device_token row
        // existed but notification_preferences row was missing. Surface
        // errors via toast.
        await updatePrefs.mutateAsync({ enabled: 1 } as Partial<Prefs>);
        toast.success(t('notifications.enabledToast'));
      } catch (err) {
        console.error('[notifications] enable flow failed', err);
        toast.error(
          err instanceof Error ? `推送启用失败: ${err.message}` : '推送启用失败',
        );
      }
    } else {
      // Disable flow — best-effort delete all tokens
      try {
        const tokens = tokensQ.data?.tokens ?? [];
        for (const token of tokens) {
          try {
            await deleteToken.mutateAsync(token.id);
          } catch {
            // best-effort, swallow errors
          }
        }
        await updatePrefs.mutateAsync({ enabled: 0 } as Partial<Prefs>);
        toast.info(t('notifications.disabledToast'));
      } catch (err) {
        console.error('[notifications] disable flow failed', err);
        toast.error(err instanceof Error ? err.message : t('notifications.disabledToast'));
      }
    }
  }

  function handleTimeChange(field: keyof Prefs, value: string) {
    updatePrefs.mutate({ [field]: value } as Partial<Prefs>);
  }

  function toggleRealtime(type: string) {
    const next = realtimeArr.includes(type)
      ? realtimeArr.filter((t) => t !== type)
      : [...realtimeArr, type];
    updatePrefs.mutate({ realtime_types: next });
  }

  // Diagnostic button — visible whenever any device token exists. Bypasses
  // the toggle's silent-fail path (see handleToggle comment): force-enables
  // prefs then fires a test push via the admin endpoint. The toast shows
  // the dispatcher's per-bucket counts so we can see exactly where the
  // delivery stops if the phone doesn't buzz.
  async function handleTestPush() {
    try {
      // 1) Force enable prefs (in case the toggle path failed silently)
      await api.put<Prefs>('/push/notification-preferences', { enabled: 1 });

      // 2) Fire test alert via admin endpoint
      const result = await api.post<{
        result: {
          pushed: number;
          suppressed_quiet: number;
          suppressed_dup: number;
          suppressed_disabled: number;
          attempts: number;
        };
        alert_id: string;
      }>('/admin/dispatch-test-alert', {
        alert_type: 'duplicate_receipt',
        severity: 'warn',
        title: '🔔 测试推送',
        body: '从 Settings 触发 · 看到这条 = FCM 通了',
      });

      const r = result.result;
      if (r.pushed > 0) {
        toast.success(`✅ 已发 ${r.pushed} 条 · 看手机锁屏`);
      } else if (r.suppressed_quiet > 0) {
        toast.info(`静默时段抑制 ${r.suppressed_quiet} 条 · 调安静时段或等 07:00 后`);
      } else if (r.suppressed_disabled > 0) {
        toast.error(`prefs 未启用或类型未勾选 · ${JSON.stringify(r)}`);
      } else {
        toast.error(`未投递 · ${JSON.stringify(r)}`);
      }
      qc.invalidateQueries({ queryKey: ['notification-prefs', ws?.id] });
    } catch (err) {
      console.error('[notifications] test push failed', err);
      toast.error(
        err instanceof Error ? `测试推送失败: ${err.message}` : '测试推送失败',
      );
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (!ws || prefsQ.isLoading) {
    return (
      <section className="rounded-2xl border bg-card p-4">
        <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border bg-card p-4 space-y-4">
      {/* Header row: title + toggle */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-medium">{t('notifications.sectionTitle')}</div>
          <div className="text-xs text-muted-foreground">{t('notifications.sectionDesc')}</div>
        </div>

        {/* Toggle switch */}
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={() => handleToggle(!enabled)}
          disabled={updatePrefs.isPending}
          className="flex min-h-[44px] shrink-0 items-center disabled:opacity-50"
        >
          <span
            className={`relative block h-6 w-11 rounded-full transition ${
              enabled ? 'bg-primary' : 'bg-muted'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                enabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </span>
        </button>
      </div>

      {/* Expanded content — only shown when enabled */}
      {enabled && (
        <>
          {/* Time settings */}
          <div className="space-y-2 border-t pt-3">
            <TimeRow
              label={t('notifications.morningTime')}
              value={prefs?.morning_briefing_local_time ?? '07:30'}
              onChange={(v) => handleTimeChange('morning_briefing_local_time', v)}
            />
            <TimeRow
              label={t('notifications.quietStart')}
              value={prefs?.quiet_start_local_time ?? '22:00'}
              onChange={(v) => handleTimeChange('quiet_start_local_time', v)}
            />
            <TimeRow
              label={t('notifications.quietEnd')}
              value={prefs?.quiet_end_local_time ?? '07:00'}
              onChange={(v) => handleTimeChange('quiet_end_local_time', v)}
            />
          </div>

          {/* Realtime types */}
          <div className="border-t pt-3">
            <div className="text-sm font-medium mb-2">{t('notifications.realtimeLabel')}</div>
            <div className="space-y-2">
              {ALL_REALTIME_TYPES.map((type) => (
                <label key={type} className="flex items-center gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={realtimeArr.includes(type)}
                    onChange={() => toggleRealtime(type)}
                  />
                  <span>{t(`notifications.type.${type}`)}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Registered devices */}
          <div className="border-t pt-3">
            <div className="text-sm font-medium mb-2">{t('notifications.devicesLabel')}</div>
            {tokensQ.isLoading ? (
              <div className="text-xs text-muted-foreground">{t('common.loading')}</div>
            ) : !tokensQ.data?.tokens.length ? (
              <div className="text-xs text-muted-foreground">{t('notifications.noDevices')}</div>
            ) : (
              <div className="space-y-2">
                {tokensQ.data.tokens.map((token) => (
                  <div key={token.id} className="rounded-lg border bg-background/60 p-2 text-sm">
                    <div className="min-w-0">
                      <span className="font-medium cjk-keep">
                        📱 {token.label ?? token.platform}
                      </span>
                      <span className="ml-2 text-xs text-muted-foreground">
                        · {token.is_self ? t('notifications.deviceOwnerSelf') : (token.user_name ?? t('notifications.deviceOwnerFamily'))}
                      </span>
                      {token.last_seen_at ? (
                        <span className="ml-2 text-xs text-muted-foreground">
                          {/* last_seen_at stored as unix seconds (Math.floor(Date.now()/1000));
                              JS Date() wants milliseconds — without *1000 every device shows
                              1970/1/22 since the seconds-since-epoch value, treated as ms,
                              is only ~20 days into 1970. */}
                          · {new Date(token.last_seen_at * 1000).toLocaleDateString()}
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const result = await api.post<{
                              success: boolean;
                              error?: string;
                              token_suffix: string;
                              target_label?: string;
                            }>('/admin/dispatch-to-device', { device_token_id: token.id });
                            if (result.success) {
                              toast.success(
                                `→ ${result.target_label ?? token.label ?? '设备'} · token …${result.token_suffix} 发了 · 看那台手机`,
                              );
                            } else {
                              toast.error(`失败 · ${result.error ?? 'unknown'}`);
                            }
                          } catch (e) {
                            toast.error(e instanceof Error ? e.message : '发送失败');
                          }
                        }}
                        className="inline-flex min-h-[44px] items-center rounded-md border px-3 text-xs text-primary hover:bg-primary/5"
                      >
                        🔔 测试
                      </button>
                      {token.is_self ? (
                        <button
                          type="button"
                          onClick={async () => {
                            if (
                              await confirmDialog({
                                title: t('notifications.removeDeviceConfirm'),
                                destructive: true,
                              })
                            )
                              deleteToken.mutate(token.id);
                          }}
                          disabled={deleteToken.isPending}
                          className="inline-flex min-h-[44px] items-center rounded-md border border-destructive/40 px-3 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-50"
                        >
                          {t('notifications.removeDevice')}
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Diagnostic: send a test push to all registered devices. Always
          visible when any token exists, even if the main toggle says off —
          this is the bypass for "toggle saved silently failed" scenarios. */}
      {(tokensQ.data?.tokens.length ?? 0) > 0 && (
        <div className="border-t pt-3">
          <button
            type="button"
            onClick={handleTestPush}
            className="w-full rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm font-medium text-primary hover:bg-primary/10"
          >
            🔔 发送测试推送
          </button>
          <p className="mt-1.5 text-xs text-muted-foreground">
            按一下：自动启用 + 发一条假"重复小票"提醒到手机锁屏。
          </p>
        </div>
      )}
    </section>
  );
}
